import React, { useEffect, useRef, useState } from 'react';
import { REGIONS, regionByCode, DEFAULT_REGION } from '../services/regions';
import { useRegion } from '../contexts/RegionContext';

/**
 * "Specify region" dropdown for the top nav (between the Movies/Series toggle and
 * the Login button). Drives streaming-availability ranking + the "Available on"
 * badge. Custom popover (not a native <select>) to match the editorial dark theme.
 *
 * Props:
 *   compact — denser padding + show the country CODE instead of the full name
 *             (used in the tighter results-view sticky nav).
 */
const RegionSelector = ({ compact = false }) => {
  const { region, setRegion } = useRegion();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = regionByCode(region) || regionByCode(DEFAULT_REGION);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Region: ${current.name}`}
        className="flex items-center gap-1.5 rounded-full hover:border-[rgba(241,236,226,0.4)] transition-colors"
        style={{ border: '1px solid rgba(241,236,226,0.16)', padding: compact ? '6px 10px' : '7px 12px', fontSize: compact ? 12 : 13, color: '#C9C2B4', background: 'transparent', whiteSpace: 'nowrap', cursor: 'pointer' }}
      >
        <span style={{ fontSize: compact ? 13 : 14, lineHeight: 1 }}>{current.flag}</span>
        <span className="hidden sm:inline">{compact ? current.code : current.name}</span>
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.55, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-[60] mt-2 overflow-y-auto rounded-lg animate-fadein"
          style={{ background: '#1C181C', border: '1px solid rgba(241,236,226,0.14)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)', minWidth: 210, maxHeight: 360 }}
        >
          <div style={{ padding: '9px 14px 6px', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6E675D' }}>
            Specify region
          </div>
          {REGIONS.map((r) => {
            const active = r.code === region;
            return (
              <button
                key={r.code}
                role="option"
                aria-selected={active}
                onClick={() => { setRegion(r.code); setOpen(false); }}
                className="flex items-center gap-2.5 w-full text-left transition-colors"
                style={{ padding: '8px 14px', fontSize: 13.5, background: active ? 'rgba(220,168,94,0.12)' : 'transparent', color: active ? '#DCA85E' : '#C9C2B4', border: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(241,236,226,0.05)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 15, lineHeight: 1 }}>{r.flag}</span>
                <span style={{ flex: 1 }}>{r.name}</span>
                {active && <span style={{ fontSize: 11 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RegionSelector;
